const API_BASE_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this._refreshPromise = null; // shared in-flight refresh — prevents concurrent-401 race
  }

  // ── Token helpers ─────────────────────────────────────────
  getAccess()  { return localStorage.getItem('accessToken'); }
  getRefresh() { return localStorage.getItem('refreshToken'); }

  storeTokens(access, refresh) {
    localStorage.setItem('accessToken', access);
    if (refresh) localStorage.setItem('refreshToken', refresh);
  }

  clearTokens() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // ── Refresh access token ──────────────────────────────────
  async refreshAccessToken() {
    // If a refresh is already in flight, every concurrent caller awaits the
    // SAME promise instead of each starting its own — this is what was
    // causing the intermittent 401s when several polls hit at once.
    if (this._refreshPromise) return this._refreshPromise;

    const refreshToken = this.getRefresh();
    if (!refreshToken) return null;

    this._refreshPromise = (async () => {
      try {
        const res  = await fetch(`${this.baseURL}/auth/refresh`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ refreshToken }),
        });
        if (!res.ok) return null;
        const data = await res.json();
        this.storeTokens(data.accessToken, data.refreshToken);
        return data.accessToken;
      } catch {
        return null;
      } finally {
        this._refreshPromise = null;
      }
    })();

    return this._refreshPromise;
  }

  // ── Core request with auto token refresh ──────────────────
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const makeRequest = (token) => {
      const headers = { 'Content-Type': 'application/json', ...options.headers };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      return fetch(url, { ...options, headers });
    };

    let res = await makeRequest(this.getAccess());

    // Token expired — try refresh once
    if (res.status === 401) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        res = await makeRequest(newToken);
      } else {
        // Refresh failed — force logout
        this.clearTokens();
        window.location.href = '/login';
        return;
      }
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      throw new Error(err.error || err.message || `HTTP ${res.status}`);
    }

    return res.json();
  }

  // ── Auth ──────────────────────────────────────────────────
  async login(email, password, businessId) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password, businessId }),
    });
  }

  async activate(email, businessId, tempCode, newPassword) {
    return this.request('/auth/activate', {
      method: 'POST',
      body: JSON.stringify({ email, businessId, tempCode, newPassword }),
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  }

  async invite(email, firstName, lastName, role, teamId) {
    return this.request('/auth/invite', {
      method: 'POST',
      body: JSON.stringify({ email, firstName, lastName, role, teamId }),
    });
  }

  async updateUser(id, data) {
    return this.request(`/auth/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async deleteUser(id) {
    return this.request(`/auth/users/${id}`, { method: 'DELETE' });
  }
  async resendInvite(id) {
    return this.request(`/auth/users/${id}/resend-invite`, { method: 'POST' });
  }
  async inviteEmployee(employeeId, email) {
    return this.request(`/auth/employees/${employeeId}/invite`, { method: 'POST', body: JSON.stringify({ email }) });
  }

  // ── AI-generated modules (dynamically mounted, see backend/services/dynamicModuleRouter.js) ──
  async moduleRequest(companyId, apiPath, subpath = '', options = {}) {
    return this.request(`/api/modules/${encodeURIComponent(companyId)}/${encodeURIComponent(apiPath)}${subpath}`, options);
  }

  // ── AIM (Actionable Item Management) ───────────────────────
  async getAimDefaults() { return this.request('/api/aim/defaults'); }
  async updateAimPreferences(prefs) {
    return this.request('/api/aim/preferences', { method: 'PATCH', body: JSON.stringify(prefs) });
  }
  async getAimDomain(domain, scopeType, scopeId) {
    const params = new URLSearchParams();
    if (scopeType) params.set('scopeType', scopeType);
    if (scopeId) params.set('scopeId', scopeId);
    const qs = params.toString();
    return this.request(`/api/aim/${domain}${qs ? `?${qs}` : ''}`);
  }
  async acknowledgeOperationalAlert(id) {
    return this.request(`/api/aim/operational-alerts/${id}/acknowledge`, { method: 'POST' });
  }
  async assignScheduling(clientId, assignments) {
    return this.request('/api/aim/scheduling/assign', {
      method: 'POST',
      body: JSON.stringify({ clientId, assignments })
    });
  }
  async resolveOperationalAlert(id) {
    return this.request(`/api/aim/operational-alerts/${id}/resolve`, { method: 'POST' });
  }

  // ── Schedule ────────────────────────────────────────────────
  async getScheduleDefaults() { return this.request('/api/schedule/defaults'); }
  async getScheduleView(weekStart) {
    const qs = weekStart ? `?weekStart=${encodeURIComponent(weekStart)}` : '';
    return this.request(`/api/schedule/view${qs}`);
  }
  async proposeAssignShift(clientId, employeeId, date, startTime, endTime, sourceAssignmentId) {
    return this.request('/api/schedule/assign-shift', {
      method: 'POST',
      body: JSON.stringify({ clientId, employeeId, date, startTime, endTime, sourceAssignmentId })
    });
  }
  async proposeMoveShift(shiftId, to) {
    return this.request('/api/schedule/move-shift', { method: 'POST', body: JSON.stringify({ shiftId, to }) });
  }
  async proposeCancelShift(shiftId) {
    return this.request('/api/schedule/cancel-shift', { method: 'POST', body: JSON.stringify({ shiftId }) });
  }

  // ── Business Data ─────────────────────────────────────────
  async getBusinessData() { return this.request('/api/business-data'); }
  async updateBusinessData(data) {
    return this.request('/api/business-data', { method: 'PUT', body: JSON.stringify(data) });
  }

  // ── Tenant Branding ───────────────────────────────────────
  async getBranding() { return this.request('/api/tenant/branding'); }
  async updateBranding(data) {
    return this.request('/api/tenant/branding', { method: 'PUT', body: JSON.stringify(data) });
  }

  // ── Personal Theme Overrides ───────────────────────────────
  async getMyTheme() { return this.request('/api/my-theme'); }
  async updateMyTheme(data) {
    return this.request('/api/my-theme', { method: 'PUT', body: JSON.stringify(data) });
  }

  // ── Employees ─────────────────────────────────────────────
  async getEmployees() { return this.request('/api/employees'); }
  async createEmployee(data) {
    return this.request('/api/employees', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateEmployee(id, data) {
    return this.request(`/api/employees/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteEmployee(id) {
    return this.request(`/api/employees/${id}`, { method: 'DELETE' });
  }

  // ── Chat ──────────────────────────────────────────────────
  async sendChatMessage(message) {
    return this.request('/api/chat', { method: 'POST', body: JSON.stringify({ message }) });
  }
  async getChatHistory() { return this.request('/api/chat/history'); }

  // ── Modules ───────────────────────────────────────────────
  async getModules() { return this.request('/api/modules'); }
  async createModule(data) {
    return this.request('/api/modules', { method: 'POST', body: JSON.stringify(data) });
  }
  async deleteModule(id) { return this.request(`/api/modules/${id}`, { method: 'DELETE' }); }

  // ── File Upload ───────────────────────────────────────────
  async uploadFile(file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/api/upload', { method: 'POST', headers: {}, body: formData });
  }

  // ── Security ─────────────────────────────────────────────
  async getSecurityAlerts() { return this.request('/api/security-alerts'); }

  // ── Analytics ─────────────────────────────────────────────
  async getAnalytics()        { return this.request('/api/analytics'); }
  async getPerformanceMetrics() { return this.request('/api/metrics'); }
}

// ─── Global fetch interceptor — attaches JWT to all raw fetch calls ──────────
const _origFetch = window.fetch.bind(window);
window.fetch = async (url, options = {}) => {
  const token = localStorage.getItem('accessToken');
  const isExternal = typeof url === 'string' && (
    url.startsWith('http') && !url.includes('localhost:3000')
  );
  if (token && !isExternal) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };
  }
  return _origFetch(url, options);
};
// ─────────────────────────────────────────────────────────────────────────────
export default new ApiService();
